import { call, fork, put } from 'redux-saga/effects';
import { whenCapable } from '../Feature/capable';
import { ManagementCapabilities } from '@signageos/common-types/dist/Device/Capabilities/ManagementCapabilities';
import ManagementCapability from '../NativeDevice/Management/ManagementCapability';
import { withDependencies } from '../DI/dependencyInjection';
import Responsibility from '../Feature/Responsibility';
import { whenResponsible } from '../Feature/responsible';
import OfflineCache from '../OfflineCache/OfflineCache';
import { getConfiguration } from '../CustomScripts/utils';
import { AppletBinaryDownloader } from '../Front/Applet/AppletBinaryDownloader';
import { IFile } from '../NativeDevice/fileSystem';
import { bindAndTakeEvery, bindAndTakeEveryOnlyWhenPlatform } from '../Socket/socketActionCreator';
import { RunnerDownloader } from './runnerDownloader';
import {
	AssignDeviceRunner,
	RunnerGet,
	RunnerGetFailed,
	RunnerGetSucceeded,
	RunnerSet,
	RunnerSetFailed,
	RunnerSetSucceeded,
	UnAssignDeviceRunner,
	UpdateAssignDeviceRunner,
	ReloadRunner,
	UpdateAssignedRunner,
	RunnerReloadSucceeded,
	RunnerReloadFailed,
} from '@signageos/actions/dist/Device/Runner/runnerActions';
import { BrowserRuntime } from '../CustomScripts/Runtimes/browserRuntime';
import Property from '../Property/Property';
import { channel } from 'redux-saga';
import { AuthenticationSucceed } from '@signageos/actions/dist/Authentication/authenticationActions';
import { IPropertyStorage } from '../Property/propertyStorage';

// Constants for better maintainability
const RUNNER_CHECK_INTERVAL = 200;
const RUNNER_MAX_RETRIES = 20;

export interface AssignedRunners {
	[assignmentUid: string]: {
		archiveUri: string;
		runnerVersion: string;
		mainFile: string;
		runnerUid: string;
		assignmentUid: string;
		md5Checksum: string;
		runtime: string;
		appletJsFile?: IFile;
		runnerFile?: IFile;
		configuration?: Record<string, any>;
		encryptedConfigurationFields?: string[];
		assignedAt?: string;
		active: boolean;
		iframeId?: string;
		lastExecuted?: string;
	};
}

function* getAssignedRunners(propertyStorage: IPropertyStorage): any {
	return yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
}

function* saveRunner(propertyStorage: IPropertyStorage, assignmentUid: string, runnerData: AssignedRunners[string]): any {
	const currentRunners = yield getAssignedRunners(propertyStorage);
	const updatedRunners = {
		...currentRunners,
		[assignmentUid]: {
			...runnerData,
			assignedAt: new Date().toISOString(),
		},
	};
	yield propertyStorage.setValue(Property.DEVICE_RUNNERS, updatedRunners);
}

function* removeRunner(propertyStorage: IPropertyStorage, assignmentUid: string): any {
	const currentRunners = yield getAssignedRunners(propertyStorage);
	const { [assignmentUid]: _removed, ...remainingRunners } = currentRunners;
	yield propertyStorage.setValue(Property.DEVICE_RUNNERS, remainingRunners);
}

/**
 * Utility function to wait for sosRunner module to be available in iframe
 */
function* waitForSosRunner(iframeId: string, runnerUid?: string, methodName: string = 'run'): any {
	const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
	if (!iframe?.contentWindow) {
		throw new Error(`Iframe with id ${iframeId} not found or has no contentWindow`);
	}

	const iframeWindow = iframe.contentWindow as any;

	const waitForModule = (retries = RUNNER_MAX_RETRIES): Promise<any> => {
		return new Promise((resolve, reject) => {
			const checkModule = () => {
				if (iframeWindow?.sosRunner?.[methodName]) {
					resolve(iframeWindow.sosRunner);
				} else if (retries > 0) {
					setTimeout(checkModule, RUNNER_CHECK_INTERVAL);
					retries--;
				} else {
					reject(new Error(`sosRunner.${methodName} not available for runner ${runnerUid || 'unknown'}`));
				}
			};
			checkModule();
		});
	};

	return yield call(waitForModule);
}

/**
 * Utility function to get iframe and its window
 */
function getIframeWindow(iframeId: string): { iframe: HTMLIFrameElement | null; iframeWindow: any } {
	const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
	const iframeWindow = iframe?.contentWindow;
	return { iframe, iframeWindow };
}

/**
 * Utility function to handle runner execution in iframe
 */
function* executeRunnerMethod(runnerData: AssignedRunners[string], methodName: 'get' | 'set', data?: Record<string, any>): any {
	const { iframe, iframeWindow } = getIframeWindow(runnerData.md5Checksum);

	if (!iframe || !iframeWindow) {
		throw new Error(`Iframe not found for runner ${runnerData.runnerUid}`);
	}

	const sosRunner = yield waitForSosRunner(runnerData.md5Checksum, runnerData.runnerUid, methodName);

	if (methodName === 'set' && data !== undefined) {
		return yield call(sosRunner.set, data);
	} else if (methodName === 'get') {
		return yield call(sosRunner.get);
	}

	throw new Error(`Unsupported method: ${methodName}`);
}

export const runnersSaga = whenResponsible(Responsibility.RUNNERS, whenCapable(ManagementCapabilities.RUNNERS, runnersSagaDefault));

function* runnersSagaDefault(offlineCache: OfflineCache) {
	yield fork(
		whenCapable(
			ManagementCapability.RUNNERS,
			withDependencies(
				['managementDriver', 'staticBaseUrl', 'propertyStorage', 'featureFlags'],
				function* ({ managementDriver, staticBaseUrl, propertyStorage, featureFlags }) {
					if (!(featureFlags?.runners ?? true)) {
						return;
					}

					const getStaticBaseUrl = () => {
						return staticBaseUrl;
					};

					const browserRuntime = new BrowserRuntime();
					const runnerDownloader = new RunnerDownloader(offlineCache, getStaticBaseUrl);

					const appletBinaryDownloader = new AppletBinaryDownloader(false, offlineCache, getStaticBaseUrl);

					yield bindAndTakeEveryOnlyWhenPlatform(RunnerSet, function* (action: RunnerSet) {
						try {
							const assignedRunners: AssignedRunners = yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
							const findRunner = assignedRunners[action.assignmentUid];
							if (!findRunner) {
								yield put<RunnerSetFailed>({
									type: RunnerSetFailed,
									uid: action.uid,
									error: `Runner with assignment UID ${action.assignmentUid} not found.`,
								});
								return;
							}

							let encryptedDataFields: Record<string, any> = Object.assign({}, action.data);
							if (action.encryptedDataFields && action.encryptedDataFields.length > 0) {
								encryptedDataFields = yield call(getConfiguration, action.data, action.encryptedDataFields, managementDriver);
							}

							// Check if runner iframe already exists
							const { iframe, iframeWindow } = getIframeWindow(findRunner.md5Checksum);

							if (iframe && iframeWindow) {
								// Runner already loaded, call set directly
								try {
									yield executeRunnerMethod(findRunner, 'set', encryptedDataFields);
									yield put<RunnerSetSucceeded>({
										type: RunnerSetSucceeded,
										uid: action.uid,
									});
								} catch (error) {
									yield put<RunnerSetFailed>({
										type: RunnerSetFailed,
										uid: action.uid,
										error: error.message,
									});
								}
							} else {
								// Need to load runner first
								const configuration: Record<string, any> = yield call(
									getConfiguration,
									findRunner.configuration ?? {},
									findRunner.encryptedConfigurationFields || [],
									managementDriver,
								);

								yield call(
									browserRuntime.runPluginInBrowser,
									managementDriver,
									findRunner,
									configuration ?? {},
									findRunner.runnerFile,
									findRunner.appletJsFile,
								);

								// Wait for iframe to be ready
								yield new Promise((resolve) => setTimeout(resolve, 2000));

								try {
									yield executeRunnerMethod(findRunner, 'set', encryptedDataFields);
									yield put<RunnerSetSucceeded>({
										type: RunnerSetSucceeded,
										uid: action.uid,
									});
								} catch (error) {
									yield put<RunnerSetFailed>({
										type: RunnerSetFailed,
										uid: action.uid,
										error: error.message,
									});
								}
							}
						} catch (error) {
							yield put<RunnerSetFailed>({
								type: RunnerSetFailed,
								uid: action.uid,
								error: error.message,
							});
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(RunnerGet, function* (action: RunnerGet): any {
						try {
							const assignedRunners: AssignedRunners = yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
							const findRunner = assignedRunners[action.assignmentUid];
							if (!findRunner) {
								console.error(`Runner with assignment UID ${action.assignmentUid} not found.`);
								yield put<RunnerGetFailed>({
									type: RunnerGetFailed,
									uid: action.uid,
									error: `Runner with assignment UID ${action.assignmentUid} not found.`,
								});
								return;
							}

							// Check if runner iframe already exists
							const { iframe, iframeWindow } = getIframeWindow(findRunner.md5Checksum);

							if (iframe && iframeWindow) {
								// Runner already loaded, call get directly
								try {
									const result = yield executeRunnerMethod(findRunner, 'get');
									yield put<RunnerGetSucceeded>({
										type: RunnerGetSucceeded,
										uid: action.uid,
										data: result,
									});
								} catch (error) {
									yield put<RunnerGetFailed>({
										type: RunnerGetFailed,
										uid: action.uid,
										error: error.message,
									});
								}
							} else {
								// Need to load runner first
								const configuration: Record<string, any> = yield call(
									getConfiguration,
									findRunner.configuration ?? {},
									findRunner.encryptedConfigurationFields || [],
									managementDriver,
								);

								yield call(
									browserRuntime.runPluginInBrowser,
									managementDriver,
									findRunner,
									configuration ?? {},
									findRunner.runnerFile,
									findRunner.appletJsFile,
								);

								// Wait for iframe to be ready
								yield new Promise((resolve) => setTimeout(resolve, 2000));

								try {
									const getData = yield executeRunnerMethod(findRunner, 'get');
									yield put<RunnerGetSucceeded>({
										type: RunnerGetSucceeded,
										uid: action.uid,
										data: getData,
									});
								} catch (error) {
									yield put<RunnerGetFailed>({
										type: RunnerGetFailed,
										uid: action.uid,
										error: error.message,
									});
								}
							}
						} catch (error) {
							yield put<RunnerGetFailed>({
								type: RunnerGetFailed,
								uid: action.uid,
								error: error.message,
							});
						}
					});

					yield bindAndTakeEvery(AssignDeviceRunner, function* (action: AssignDeviceRunner): any {
						let runnerFile: IFile | undefined = yield runnerDownloader.getOrDownloadRunner(
							action.runner.archiveUri,
							action.runner.mainFile,
							action.runner.md5Checksum,
						);

						let appletJsFile: IFile | undefined = undefined;

						if (action.frontAppletVersion) {
							appletJsFile = yield appletBinaryDownloader.getOrDownloadFrontAppletJsFile(action.frontAppletVersion);
						}

						// Save initial runner state (inactive)
						yield call(saveRunner, propertyStorage, action.assignmentUid, {
							archiveUri: action.runner.archiveUri,
							mainFile: action.runner.mainFile,
							runnerFile,
							appletJsFile,
							assignmentUid: action.assignmentUid,
							runnerUid: action.runnerUid,
							runnerVersion: action.runnerVersion,
							md5Checksum: action.runner.md5Checksum,
							runtime: action.runner.runtime,
							configuration: action.configuration,
							encryptedConfigurationFields: action.encryptedConfigurationFields,
							active: false,
							iframeId: undefined,
							lastExecuted: undefined,
						});

						let iframeId;
						const messageChannel = channel<MessageEvent>();
						const messagesHandler = (event: MessageEvent) => {
							messageChannel.put(event);
						};
						const telemetry = (keyOrObject: any, value?: any) => {
							let messageResult;
							if (typeof keyOrObject === 'object') {
								messageResult = keyOrObject;
							} else {
								messageResult = { [keyOrObject]: value };
							}
							parent.postMessage({ telemetryData: messageResult, assignmentUid: action.assignmentUid }, '*');
						};

						const configuration: Record<string, any> = yield call(
							getConfiguration,
							action.configuration,
							action.encryptedConfigurationFields || [],
							managementDriver,
						);

						try {
							yield call(
								browserRuntime.runPluginInBrowser,
								managementDriver,
								action.runner,
								configuration,
								runnerFile,
								appletJsFile,
								messagesHandler,
								telemetry,
							);
						} catch (error) {
							console.error(`Error occurred while running plugin in browser: ${error}`);
						}

						iframeId = action.runner.md5Checksum;

						yield fork(browserRuntime.bindCustomScriptsMessages, messageChannel, window, iframeId);

						// Use utility function to wait and run
						try {
							const runner = yield waitForSosRunner(iframeId, action.runner.runtime);
							yield call(runner.run);

							yield call(saveRunner, propertyStorage, action.assignmentUid, {
								archiveUri: action.runner.archiveUri,
								mainFile: action.runner.mainFile,
								runnerFile,
								assignmentUid: action.assignmentUid,
								appletJsFile,
								runnerUid: action.runnerUid,
								runnerVersion: action.runnerVersion,
								md5Checksum: action.runner.md5Checksum,
								runtime: action.runner.runtime,
								configuration: action.configuration,
								encryptedConfigurationFields: action.encryptedConfigurationFields,
								active: true,
								iframeId: action.runner.md5Checksum,
								lastExecuted: undefined,
							});
						} catch (error) {
							console.error(`Failed to initialize runner ${action.runner.runtime}: ${error.message}`);
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(UnAssignDeviceRunner, function* (action: UnAssignDeviceRunner): any {
						// Handle the UnAssignDeviceRunner action
						const assignedRunners: AssignedRunners = yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
						const findRunner = assignedRunners[action.assignmentUid];
						if (!findRunner) {
							yield call(removeRunner, propertyStorage, action.assignmentUid);
							return;
						} else {
							const iframeId = findRunner.md5Checksum;
							let iframe = document.getElementById(iframeId!) as HTMLIFrameElement;
							if (iframe.parentNode) {
								iframe.parentNode.removeChild(iframe);
							}
							yield call(removeRunner, propertyStorage, action.assignmentUid);
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(UpdateAssignDeviceRunner, function* (action: UpdateAssignDeviceRunner): any {
						// Handle the UpdateAssignDeviceRunner action - updating configuration only
						const assignedRunners: AssignedRunners = yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
						const existingRunner = assignedRunners[action.assignmentUid];

						if (!existingRunner) {
							console.error(`Runner with assignment UID ${action.assignmentUid} not found for update.`);
							return;
						}

						const updatedRunner: AssignedRunners[string] = {
							...existingRunner,
							...action,
							configuration: action.configuration,
							encryptedConfigurationFields: action.encryptedConfigurationFields,
						};

						const newConfiguration: Record<string, any> = yield call(
							getConfiguration,
							action.configuration,
							action.encryptedConfigurationFields || [],
							managementDriver,
						);

						yield call(saveRunner, propertyStorage, action.assignmentUid, updatedRunner);

						// If runner is active, notify it about configuration change
						if (existingRunner.active && existingRunner.iframeId) {
							const { iframe, iframeWindow } = getIframeWindow(existingRunner.md5Checksum);

							if (iframe && iframeWindow) {
								// Try to call the configuration change listener
								if (iframeWindow.onConfigChange) {
									try {
										yield call(iframeWindow.onConfigChange, newConfiguration);
									} catch (error) {
										console.warn('Error calling configuration change listener:', error);
									}
								} else {
									console.warn('onConfigChange listener not found in iframe');
								}
							} else {
								console.warn(`Iframe not found for active runner: ${existingRunner.md5Checksum}`);
							}
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(AuthenticationSucceed, function* (): any {
						const assignedRunners: AssignedRunners = yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
						const runnerEntries = Object.entries(assignedRunners);
						if (runnerEntries.length === 0) {
							return;
						}

						for (const [assignmentUid, runner] of runnerEntries) {
							try {
								const iframeId = runner.md5Checksum;
								const { iframe } = getIframeWindow(iframeId);

								// If iframe doesn't exist, create it
								if (!iframe) {
									const messageChannel = channel<MessageEvent>();
									const messagesHandler = (event: MessageEvent) => {
										if (event.data.assignmentUid) {
											window.localStorage.setItem(`runner_${event.data.assignmentUid}`, JSON.stringify(event.data));
										}
										messageChannel.put(event);
									};

									const telemetry = (keyOrObject: any, value?: any) => {
										let messageResult;
										if (typeof keyOrObject === 'object') {
											messageResult = keyOrObject;
										} else {
											messageResult = { [keyOrObject]: value };
										}
										parent.postMessage({ telemetryData: messageResult, assignmentUid }, '*');
									};

									const newConfiguration: Record<string, any> = yield call(
										getConfiguration,
										runner.configuration ?? {},
										runner.encryptedConfigurationFields || [],
										managementDriver,
									);

									yield call(
										browserRuntime.runPluginInBrowser,
										managementDriver,
										runner,
										newConfiguration,
										runner.runnerFile,
										runner.appletJsFile,
										messagesHandler,
										telemetry,
									);

									yield fork(browserRuntime.bindCustomScriptsMessages, messageChannel, window, iframeId);

									// Wait for iframe to load
									yield new Promise((resolve) => setTimeout(resolve, 2000));
								}

								// Use utility function to wait and run
								try {
									const iframeRunner = yield waitForSosRunner(iframeId, runner.runnerUid);
									yield call(iframeRunner.run);

									// Update runner state to active
									yield call(saveRunner, propertyStorage, assignmentUid, {
										...runner,
										active: true,
										iframeId: runner.md5Checksum,
										lastExecuted: undefined,
									});

									return yield iframeRunner.run();
								} catch (error) {
									console.warn(`Failed to initialize runner ${runner.runnerUid}: ${error.message}`);
								}
							} catch (error) {
								console.warn(`Failed to get telemetry from runner ${runner.runnerUid}:`, error);
							}
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(ReloadRunner, function* (action: ReloadRunner) {
						try {
							const assignedRunners: AssignedRunners = yield propertyStorage.getValueOrDefault(Property.DEVICE_RUNNERS, {});
							const assignmentUid = action.assignmentUid;
							const storedRunner = assignedRunners[assignmentUid];

							if (!storedRunner) {
								console.warn(`Runner with assignmentUid ${assignmentUid} not found for reload`);
								return;
							}
							let needsUpdate = false;

							if (
								action.runner &&
								(action.runner.archiveUri !== storedRunner.archiveUri ||
									action.runner.mainFile !== storedRunner.mainFile ||
									action.runner.md5Checksum !== storedRunner.md5Checksum)
							) {
								needsUpdate = true;
							}

							// If no updates needed, we can skip the reload
							if (!needsUpdate) {
								yield put<RunnerReloadSucceeded>({
									type: RunnerReloadSucceeded,
									uid: action.uid,
								});
								return;
							}

							// Remove existing iframe if it exists
							const existingIframe = document.getElementById(storedRunner.md5Checksum) as HTMLIFrameElement;
							if (existingIframe?.parentNode) {
								existingIframe.parentNode.removeChild(existingIframe);
							}

							// Re-download runner files to get latest version
							let runnerFile: IFile | undefined = yield runnerDownloader.getOrDownloadRunner(
								action.runner.archiveUri,
								action.runner.mainFile,
								action.runner.md5Checksum,
							);

							let appletJsFile: IFile | undefined = storedRunner.appletJsFile;

							// Update stored runner with new files
							const updatedRunner: AssignedRunners[string] = {
								...storedRunner,
								...action.runner,
								runnerFile,
								appletJsFile,
								active: false,
								iframeId: undefined,
								lastExecuted: undefined,
							};
							yield call(saveRunner, propertyStorage, assignmentUid, updatedRunner);

							// Create new iframe and run the updated runner
							const messageChannel = channel<MessageEvent>();
							const messagesHandler = (event: MessageEvent) => {
								messageChannel.put(event);
							};

							const configuration: Record<string, any> = yield call(
								getConfiguration,
								storedRunner.configuration ?? {},
								storedRunner.encryptedConfigurationFields || [],
								managementDriver,
							);

							try {
								yield call(
									browserRuntime.runPluginInBrowser,
									managementDriver,
									updatedRunner,
									configuration,
									runnerFile,
									appletJsFile,
									messagesHandler,
								);

								yield fork(browserRuntime.bindCustomScriptsMessages, messageChannel, window, updatedRunner.md5Checksum);

								// Wait for iframe to be ready and run the runner
								const sosRunner = yield waitForSosRunner(updatedRunner.md5Checksum, updatedRunner.runnerUid);
								yield call(sosRunner.run);

								// Mark runner as active
								yield call(saveRunner, propertyStorage, assignmentUid, {
									...updatedRunner,
									active: true,
									iframeId: updatedRunner.md5Checksum,
								});
								yield put<RunnerReloadSucceeded>({
									type: RunnerReloadSucceeded,
									uid: action.uid,
								});
							} catch (error) {
								console.error(`Failed to reload runner ${updatedRunner.runnerUid}:`, error);
							}
						} catch (error) {
							yield put<RunnerReloadFailed>({
								type: RunnerReloadFailed,
								uid: action.uid,
							});
							console.error('Error in ReloadRunner handler:', error);
						}
					});

					yield bindAndTakeEveryOnlyWhenPlatform(UpdateAssignedRunner, function* (action: UpdateAssignedRunner) {
						try {
							const assignedRunners: AssignedRunners = yield getAssignedRunners(propertyStorage);
							const incomingAssignments = action.assignments || [];

							const newAssignments = incomingAssignments.filter((assignment) => !assignedRunners[assignment.assignmentUid]);

							for (const assignment of newAssignments) {
								try {
									const runnerFile = yield runnerDownloader.getOrDownloadRunner(
										assignment.runner.archiveUri,
										assignment.runner.mainFile,
										assignment.runner.md5Checksum,
									);

									let appletJsFile = undefined;
									if (assignment.frontAppletVersion) {
										appletJsFile = yield appletBinaryDownloader.getOrDownloadFrontAppletJsFile(assignment.frontAppletVersion);
									}

									const runnerData = {
										archiveUri: assignment.runner.archiveUri,
										mainFile: assignment.runner.mainFile,
										runnerFile,
										assignmentUid: assignment.assignmentUid,
										appletJsFile,
										runnerUid: assignment.runnerUid,
										runnerVersion: assignment.runnerVersion,
										md5Checksum: assignment.runner.md5Checksum,
										runtime: assignment.runner.runtime,
										configuration: assignment.configuration,
										encryptedConfigurationFields: assignment.encryptedConfigurationFields,
										active: false,
										iframeId: undefined,
										lastExecuted: undefined,
									};

									yield call(saveRunner, propertyStorage, assignment.assignmentUid, runnerData);
								} catch (error) {
									console.error(`Failed to download ${assignment.assignmentUid}:`, error);
								}
							}
						} catch (error) {
							console.error('Error processing UpdateAssignedRunner:', error);
						}
					});
				},
			),
		),
	);
}
